import { deletePost, fetchReplies } from "./postsApi";
import { loadIdentity } from "./identity";

const REPLIES_PREFIX = "replies-";
const REPLY_PREFIX = "reply-";
const CONTROL_CLASS = "ol-reply-delete-control";

function normalizeIdentityId(value) {
  return String(value || "").trim().toLowerCase();
}

function idFromTestId(element, prefix) {
  const testId = element?.getAttribute?.("data-testid") || "";
  return testId.startsWith(prefix) ? testId.slice(prefix.length) : "";
}

function directReplyNodes(list) {
  return Array.from(list?.children || []).filter((child) => {
    const testId = child.getAttribute?.("data-testid") || "";
    return testId.startsWith(REPLY_PREFIX) && !testId.startsWith("reply-audio-");
  });
}

function closeOtherMenus(except) {
  document.querySelectorAll(`.${CONTROL_CLASS}[data-open="true"]`).forEach((control) => {
    if (control !== except) control.dataset.open = "false";
  });
}

function removeReplyNode(replyNode, list) {
  replyNode.remove();
  if (directReplyNodes(list).length === 0) list.remove();
}

function createDeleteControl({ replyId, replyNode, list, deletePostFn }) {
  const control = document.createElement("div");
  control.className = CONTROL_CLASS;
  control.dataset.open = "false";
  control.dataset.confirming = "false";
  control.setAttribute("data-testid", `reply-actions-${replyId}`);

  const menuButton = document.createElement("button");
  menuButton.type = "button";
  menuButton.className = "ol-reply-menu-button";
  menuButton.setAttribute("aria-label", "Azioni risposta");
  menuButton.setAttribute("aria-expanded", "false");
  menuButton.setAttribute("data-testid", `reply-menu-${replyId}`);
  menuButton.textContent = "•••";

  const panel = document.createElement("div");
  panel.className = "ol-reply-delete-panel";
  panel.setAttribute("role", "menu");

  const deleteButton = document.createElement("button");
  deleteButton.type = "button";
  deleteButton.className = "ol-reply-delete-button";
  deleteButton.setAttribute("role", "menuitem");
  deleteButton.setAttribute("data-testid", `delete-reply-${replyId}`);
  deleteButton.textContent = "Elimina risposta";

  const confirmBox = document.createElement("div");
  confirmBox.className = "ol-reply-confirm-box";

  const confirmText = document.createElement("p");
  confirmText.textContent = "Eliminare definitivamente questa risposta?";

  const confirmActions = document.createElement("div");
  confirmActions.className = "ol-reply-confirm-actions";

  const confirmButton = document.createElement("button");
  confirmButton.type = "button";
  confirmButton.className = "ol-reply-confirm-delete";
  confirmButton.setAttribute("data-testid", `confirm-delete-reply-${replyId}`);
  confirmButton.textContent = "Elimina";

  const cancelButton = document.createElement("button");
  cancelButton.type = "button";
  cancelButton.className = "ol-reply-confirm-cancel";
  cancelButton.textContent = "Annulla";

  const error = document.createElement("p");
  error.className = "ol-reply-delete-error";
  error.setAttribute("role", "alert");

  menuButton.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    const nextOpen = control.dataset.open !== "true";
    closeOtherMenus(control);
    control.dataset.open = String(nextOpen);
    control.dataset.confirming = "false";
    menuButton.setAttribute("aria-expanded", String(nextOpen));
    error.textContent = "";
  });

  deleteButton.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    control.dataset.confirming = "true";
    error.textContent = "";
  });

  cancelButton.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    control.dataset.confirming = "false";
    error.textContent = "";
  });

  confirmButton.addEventListener("click", async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (control.dataset.busy === "true") return;
    control.dataset.busy = "true";
    confirmButton.disabled = true;
    cancelButton.disabled = true;
    confirmButton.textContent = "Elimino…";
    error.textContent = "";
    try {
      await deletePostFn(replyId);
      removeReplyNode(replyNode, list);
      window.dispatchEvent(new CustomEvent("ol:reply-deleted", {
        detail: {
          postId: idFromTestId(list, REPLIES_PREFIX),
          replyId,
        },
      }));
    } catch (err) {
      const payload = err?.response?.data;
      error.textContent = payload?.message || payload?.detail || err?.message || "Impossibile eliminare la risposta";
      control.dataset.busy = "false";
      confirmButton.disabled = false;
      cancelButton.disabled = false;
      confirmButton.textContent = "Elimina";
    }
  });

  confirmActions.append(confirmButton, cancelButton);
  confirmBox.append(confirmText, confirmActions);
  panel.append(deleteButton, confirmBox, error);
  control.append(menuButton, panel);
  return control;
}

export async function decorateReplyList(list, dependencies = {}) {
  if (!list || list.dataset.replyDeleteLoading === "true") return;
  const loadIdentityFn = dependencies.loadIdentity || loadIdentity;
  const fetchRepliesFn = dependencies.fetchReplies || fetchReplies;
  const deletePostFn = dependencies.deletePost || deletePost;
  const identity = loadIdentityFn();
  const myIdentityId = normalizeIdentityId(identity?.identityId);
  const postId = idFromTestId(list, REPLIES_PREFIX);
  if (!myIdentityId || !postId) return;

  list.dataset.replyDeleteLoading = "true";
  try {
    const replies = await fetchRepliesFn(postId);
    const byId = new Map((Array.isArray(replies) ? replies : []).map((reply) => [String(reply.id), reply]));
    directReplyNodes(list).forEach((replyNode) => {
      const replyId = idFromTestId(replyNode, REPLY_PREFIX);
      const reply = byId.get(replyId);
      const ownedByMe = normalizeIdentityId(reply?.identityId) === myIdentityId;
      const existing = replyNode.querySelector(`.${CONTROL_CLASS}`);
      if (!ownedByMe) {
        existing?.remove();
        return;
      }
      if (existing) return;
      replyNode.style.position = "relative";
      replyNode.append(createDeleteControl({ replyId, replyNode, list, deletePostFn }));
    });
  } catch {
    // The feed remains usable when the replies endpoint is temporarily unavailable.
  } finally {
    list.dataset.replyDeleteLoading = "false";
  }
}

export function decorateMainDeleteWarnings(root = document) {
  const buttons = root.querySelectorAll?.('[data-testid^="confirm-delete-"]') || [];
  Array.from(buttons).forEach((button) => {
    const testId = button.getAttribute("data-testid") || "";
    if (testId.startsWith("confirm-delete-reply-")) return;
    const confirmation = button.parentElement?.parentElement;
    const message = confirmation?.querySelector?.("p");
    if (!message || message.dataset.deleteCascadeWarning === "true") return;
    message.textContent = "Eliminare definitivamente questo messaggio? Saranno eliminate anche tutte le risposte collegate.";
    message.dataset.deleteCascadeWarning = "true";
  });
}

export function decorateAllReplyLists(root = document, dependencies = {}) {
  const lists = root.querySelectorAll?.(`[data-testid^="${REPLIES_PREFIX}"]`) || [];
  return Promise.all(Array.from(lists).map((list) => decorateReplyList(list, dependencies)));
}

export function startReplyDeleteControls(dependencies = {}) {
  if (typeof document === "undefined" || typeof MutationObserver === "undefined") {
    return () => {};
  }
  let timer = null;
  const schedule = () => {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = null;
      decorateMainDeleteWarnings(document);
      void decorateAllReplyLists(document, dependencies);
    }, 50);
  };
  schedule();
  const observer = new MutationObserver(schedule);
  observer.observe(document.body, { childList: true, subtree: true });
  const closeOnOutsideClick = (event) => {
    if (event.target?.closest?.(`.${CONTROL_CLASS}`)) return;
    closeOtherMenus(null);
  };
  document.addEventListener("click", closeOnOutsideClick);
  return () => {
    if (timer) window.clearTimeout(timer);
    observer.disconnect();
    document.removeEventListener("click", closeOnOutsideClick);
  };
}

export { normalizeIdentityId };
